/**
 * @fileoverview UC-REG-297/298/299 — Lógica de presentación de los tipos de
 *   trámite con tipología / subtipología (`ProcedureType.parent`), del campo
 *   "Médico de preferencia" y del adjunto de derivación médica. Vive acá y
 *   no en los componentes (`MyProcedures.vue`, `ProceduresList.vue`,
 *   `ProcedureTypeAdmin.vue`) para que quede cubierta por tests unitarios.
 *
 *   Un tipo con `parent` es un subtipo; el solicitante elige primero la
 *   tipología y, si tiene subtipos activos, después el subtipo. Un tenant
 *   sin `parent` cargado ve el catálogo plano de siempre.
 */

/** @description Largo máximo del campo "Médico de preferencia" (igual que la API). */
export const PREFERRED_DOCTOR_MAX = 120;

/** @description `accept` del input de la orden de derivación (foto o PDF). */
export const REFERRAL_ACCEPT = "image/*,application/pdf";

/** @description Nombre del campo multipart con el que viaja la derivación. */
export const REFERRAL_FIELD = "referral";

const REFERRAL_MIME_RE =
  /^(image\/(jpeg|jpg|png|heic|heif|webp)|application\/pdf)$/i;
const REFERRAL_EXT_RE = /\.(jpe?g|png|heic|heif|webp|pdf)$/i;

/**
 * @description Id del padre de un tipo, venga como id o como objeto populado.
 * @param {Object} type - ProcedureType
 * @returns {string} '' si es de primer nivel
 */
export function typeParentId(type) {
  const p = type && type.parent;
  if (!p) return "";
  return String(typeof p === "object" ? p._id : p);
}

/**
 * @description Tipos de primer nivel (sin padre), ordenados por nombre.
 * @param {Array<Object>} types
 * @returns {Array<Object>}
 */
export function topLevelTypes(types) {
  return (types || [])
    .filter((t) => t && !typeParentId(t))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

/**
 * @description Subtipos de una tipología, ordenados por nombre.
 * @param {Array<Object>} types
 * @param {string} parentId
 * @returns {Array<Object>}
 */
export function childrenOf(types, parentId) {
  if (!parentId) return [];
  return (types || [])
    .filter((t) => t && typeParentId(t) === String(parentId))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

/**
 * @description La tipología tiene subtipos dentro de la lista dada.
 * @param {Array<Object>} types
 * @param {string} typeId
 * @returns {boolean}
 */
export function hasChildren(types, typeId) {
  return childrenOf(types, typeId).length > 0;
}

/**
 * @description Nombre completo del tipo: "Tipología / Subtipo" cuando el
 *   padre viene populado (o se puede resolver en `types`), si no el nombre.
 * @param {Object} type - ProcedureType (parent populado o id)
 * @param {Array<Object>} [types] - Catálogo para resolver el padre por id
 * @returns {string}
 */
export function typeLabel(type, types) {
  if (!type) return "";
  const name = type.name || "";
  const p = type.parent;
  let parentName = p && typeof p === "object" ? p.name : "";
  if (!parentName && p && Array.isArray(types)) {
    const found = types.find((t) => t && String(t._id) === String(p));
    parentName = found ? found.name : "";
  }
  return parentName ? `${parentName} / ${name}` : name;
}

/**
 * @description Catálogo ordenado jerárquicamente para la tabla del ABM: cada
 *   tipología seguida de sus subtipos, y al final los subtipos huérfanos.
 * @param {Array<Object>} types
 * @returns {Array<Object>}
 */
export function orderTypesByHierarchy(types) {
  const tops = topLevelTypes(types);
  const seen = new Set();
  const ordered = [];
  tops.forEach((t) => {
    ordered.push(t);
    seen.add(String(t._id));
    childrenOf(types, t._id).forEach((c) => {
      ordered.push(c);
      seen.add(String(c._id));
    });
  });
  (types || []).forEach((t) => {
    if (t && !seen.has(String(t._id))) ordered.push(t);
  });
  return ordered;
}

/**
 * @description Candidatos a "Pertenece a" en el ABM: tipologías activas de
 *   primer nivel, sin subtipos propios y distintas del tipo editado (dos
 *   niveles máximo, mismas reglas que la API).
 * @param {Array<Object>} types - Catálogo completo (incluye inactivos)
 * @param {string} [selfId] - Tipo que se está editando
 * @returns {Array<Object>}
 */
export function parentCandidates(types, selfId) {
  const self = selfId ? String(selfId) : "";
  if (self && hasChildren(types, self)) return [];
  return topLevelTypes(types).filter(
    (t) => t.isActive !== false && String(t._id) !== self
  );
}

/**
 * @description Opciones del filtro de tipo de la bandeja: una tipología con
 *   subtipos se vuelve un grupo (ella misma como "Todos" + sus subtipos); las
 *   demás van sueltas.
 * @param {Array<Object>} types
 * @returns {Array<{_id:string,label:string,group?:string}>}
 */
export function filterOptions(types) {
  const out = [];
  topLevelTypes(types).forEach((t) => {
    const kids = childrenOf(types, t._id);
    if (kids.length === 0) {
      out.push({ _id: String(t._id), label: t.name });
      return;
    }
    out.push({ _id: String(t._id), label: `${t.name} (todos)`, group: t.name });
    kids.forEach((k) =>
      out.push({ _id: String(k._id), label: k.name, group: t.name })
    );
  });
  return out;
}

/**
 * @description Mismas opciones que `filterOptions` pero agrupadas para
 *   renderizar `<optgroup>`: `{ label: null, options }` para las sueltas y
 *   `{ label: 'Cardiología', options }` por tipología con subtipos.
 * @param {Array<Object>} types
 * @returns {Array<{label:string|null,options:Array<{_id:string,label:string}>}>}
 */
export function filterOptionGroups(types) {
  const groups = [];
  filterOptions(types).forEach((o) => {
    const label = o.group || null;
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.options.push({ _id: o._id, label: o.label });
    } else {
      groups.push({ label, options: [{ _id: o._id, label: o.label }] });
    }
  });
  return groups;
}

/**
 * @description Resuelve el tipo final de una solicitud a partir del select de
 *   tipología y del de subtipo. Si la tipología tiene subtipos, el subtipo
 *   es obligatorio y tiene que pertenecerle.
 * @param {Object} params
 * @param {Array<Object>} params.types - Tipos activos
 * @param {string} params.typeId - Tipología elegida
 * @param {string} [params.subtypeId] - Subtipo elegido
 * @returns {{ok:boolean, typeId?:string, error?:string}}
 */
export function resolveSelectedType({ types, typeId, subtypeId }) {
  if (!typeId) return { ok: false, error: "Seleccioná un tipo de trámite" };
  const kids = childrenOf(types, typeId);
  if (kids.length === 0) return { ok: true, typeId: String(typeId) };
  if (!subtypeId)
    return { ok: false, error: "Elegí la subtipología del trámite" };
  if (!kids.some((k) => String(k._id) === String(subtypeId))) {
    return {
      ok: false,
      error: "El subtipo no corresponde a la tipología elegida",
    };
  }
  return { ok: true, typeId: String(subtypeId) };
}

/**
 * @description Busca un tipo por id dentro del catálogo.
 * @param {Array<Object>} types
 * @param {string} typeId
 * @returns {Object|null}
 */
export function findType(types, typeId) {
  if (!typeId) return null;
  return (
    (types || []).find((t) => t && String(t._id) === String(typeId)) || null
  );
}

/**
 * @description El tipo (ya resuelto) exige adjuntar la orden de derivación.
 * @param {Array<Object>} types
 * @param {string} typeId
 * @returns {boolean}
 */
export function requiresReferral(types, typeId) {
  const t = findType(types, typeId);
  return Boolean(t && t.requiresReferral === true);
}

/**
 * @description Un File del browser sirve como orden de derivación (foto o
 *   PDF). Si el browser no informa `type`, decide por la extensión.
 * @param {File|{name?:string,type?:string}} file
 * @returns {boolean}
 */
export function isReferralFile(file) {
  if (!file) return false;
  if (file.type) return REFERRAL_MIME_RE.test(file.type);
  return REFERRAL_EXT_RE.test(file.name || "");
}

/**
 * @description Etiqueta del adjunto según su `kind` (UC-REG-299).
 * @param {Object} file - ProcedureFile
 * @returns {string} "Derivación" o ''
 */
export function fileKindLabel(file) {
  return file && file.kind === "referral" ? "Derivación" : "";
}

/**
 * @description Texto del primer mensaje del trámite para el chat: la
 *   descripción o, si no hay, el médico de preferencia (UC-REG-298).
 * @param {Object} procedure - Procedure con description / preferredDoctor
 * @returns {string}
 */
export function requestText(procedure) {
  if (!procedure) return "";
  if (procedure.description) return procedure.description;
  if (procedure.preferredDoctor)
    return `Médico de preferencia: ${procedure.preferredDoctor}`;
  return "";
}

/**
 * @description Recorta el médico de preferencia al máximo permitido.
 * @param {string} value
 * @returns {string}
 */
export function normalizePreferredDoctor(value) {
  return (value || "").trim().slice(0, PREFERRED_DOCTOR_MAX);
}
